import React from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export interface ProjectStats {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  isActive: boolean;
  counts: {
    rundowns: number;
    events: number;
    stories: number;
    logs: number;
    participants: number;
    transcripts: number;
    chatSessions: number;
    tags: number;
    cameras: number;
  };
  usage: {
    period: string;
    services: Record<string, { calls: number; tokensIn: number; tokensOut: number }>;
    estimatedCost: number;
  };
}

const ADMIN_COLOR = "#A3E635";

const formatNumber = (n: any) => {
  if (n === null || n === undefined || isNaN(n)) return "0";
  return Number(n).toLocaleString("en-US");
};

const formatCost = (n: any) => {
  const val = Number(n || 0);
  if (val === 0) return "$0.00";
  if (val < 0.01) return `$${val.toFixed(4)}`;
  return `$${val.toFixed(2)}`;
};

const formatDate = (d: string) => {
  try {
    return new Date(d).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
};

export interface ProjectCardProps {
  project: ProjectStats;
  onEdit?: (project: ProjectStats) => void;
  onDelete?: (project: ProjectStats) => void;
  onManageRoles?: (project: ProjectStats) => void;
  onAudit?: (project: ProjectStats) => void;
}

export function ProjectCard({
  project: proj,
  onEdit,
  onDelete,
  onManageRoles,
  onAudit,
}: ProjectCardProps) {
  const { t } = useTranslation();

  const stats = [
    {
      key: "rundowns",
      label: t("admin.layout.rundowns"),
      value: proj.counts?.rundowns || 0,
      icon: "bx-calendar",
      color: "#F59E0B",
      info: t("admin.layout.rundownsDesc"),
    },
    {
      key: "events",
      label: t("admin.layout.eventsLabel"),
      value: proj.counts?.events || 0,
      icon: "bx-flag",
      color: "#3B82F6",
      info: t("admin.layout.eventsDesc"),
    },
    {
      key: "stories",
      label: t("admin.layout.stories"),
      value: proj.counts?.stories || 0,
      icon: "bx-book-open",
      color: "#10B981",
      info: t("admin.layout.storiesDesc"),
    },
    {
      key: "logs",
      label: t("admin.layout.logs"),
      value: proj.counts?.logs || 0,
      icon: "bx-edit-alt",
      color: "#f72d60",
      info: t("admin.layout.logsDesc"),
    },
    {
      key: "transcripts",
      label: t("admin.layout.transcripts"),
      value: proj.counts?.transcripts || 0,
      icon: "bx-microphone",
      color: "#A855F7",
      info: t("admin.layout.transcriptsDesc"),
    },
    {
      key: "ai-sessions",
      label: t("admin.layout.aiSessions"),
      value: proj.counts?.chatSessions || 0,
      icon: "bx-bot",
      color: ADMIN_COLOR,
      info: t("admin.layout.aiSessionsDesc"),
    },
    {
      key: "tags",
      label: t("admin.layout.tagsLabel"),
      value: proj.counts?.tags || 0,
      icon: "bx-purchase-tag",
      color: "#F97316",
      info: t("admin.layout.tagsDesc"),
    },
  ];

  return (
    <div
      data-testid={`admin-project-card-${proj.id}`}
      style={{
        background: "var(--color-panel-bg)",
        border: "1px solid var(--color-panel-border)",
        borderRadius: "16px",
        padding: "25px",
        transition: "all 0.3s ease",
        position: "relative",
      }}
    >
      {/* Project Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <div
            style={{
              width: "50px",
              height: "50px",
              background:
                "linear-gradient(135deg, rgba(247, 45, 96, 0.2), rgba(247, 45, 96, 0.05))",
              border: "2px solid var(--color-accent-primary)",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "var(--color-accent-primary)",
            }}
          >
            <i className="bx bx-data"></i>
          </div>
          <div>
            <div
              data-testid={`admin-project-name-${proj.id}`}
              style={{
                fontSize: "1.2rem",
                fontWeight: "800",
                letterSpacing: "1px",
                color: "var(--color-text-primary)",
              }}
            >
              {proj.name}
            </div>
            <div
              data-testid={`admin-project-meta-${proj.id}`}
              style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginTop: "3px" }}
            >
              {proj.description || "No description"} · Created: {formatDate(proj.createdAt)}
            </div>
          </div>
        </div>
        <TooltipProvider delay={200}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {onEdit && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      data-testid={`admin-project-edit-btn-${proj.id}`}
                      onClick={() => onEdit(proj)}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(163, 230, 53, 0.2)",
                        color: "var(--color-text-muted)",
                        cursor: "pointer",
                        borderRadius: "8px",
                        padding: "6px 10px",
                        fontSize: "0.85rem",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#A3E635";
                        e.currentTarget.style.color = "#A3E635";
                        e.currentTarget.style.background = "rgba(163, 230, 53, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "rgba(163, 230, 53, 0.2)";
                        e.currentTarget.style.color = "var(--color-text-muted)";
                        e.currentTarget.style.background = "transparent";
                      }}
                    />
                  }
                >
                  <i className="bx bx-edit"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("admin.layout.editProject")}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {onManageRoles && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      data-testid={`admin-project-roles-btn-${proj.id}`}
                      onClick={() => onManageRoles(proj)}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(168, 85, 247, 0.2)",
                        color: "var(--color-text-muted)",
                        cursor: "pointer",
                        borderRadius: "8px",
                        padding: "6px 10px",
                        fontSize: "0.85rem",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#A855F7";
                        e.currentTarget.style.color = "#A855F7";
                        e.currentTarget.style.background = "rgba(168, 85, 247, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "rgba(168, 85, 247, 0.2)";
                        e.currentTarget.style.color = "var(--color-text-muted)";
                        e.currentTarget.style.background = "transparent";
                      }}
                    />
                  }
                >
                  <i className="bx bx-user-circle"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("admin.layout.manageRoles")}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {onAudit && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      data-testid={`admin-project-audit-btn-${proj.id}`}
                      onClick={() => onAudit(proj)}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(234, 179, 8, 0.2)",
                        color: "var(--color-text-muted)",
                        cursor: "pointer",
                        borderRadius: "8px",
                        padding: "6px 10px",
                        fontSize: "0.85rem",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#EAB308";
                        e.currentTarget.style.color = "#EAB308";
                        e.currentTarget.style.background = "rgba(234, 179, 8, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "rgba(234, 179, 8, 0.2)";
                        e.currentTarget.style.color = "var(--color-text-muted)";
                        e.currentTarget.style.background = "transparent";
                      }}
                    />
                  }
                >
                  <i className="bx bx-history"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("admin.layout.auditLogs")}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      data-testid={`admin-project-delete-btn-${proj.id}`}
                      onClick={() => onDelete(proj)}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(239, 68, 68, 0.2)",
                        color: "var(--color-text-muted)",
                        cursor: "pointer",
                        borderRadius: "8px",
                        padding: "6px 10px",
                        fontSize: "0.85rem",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#EF4444";
                        e.currentTarget.style.color = "#EF4444";
                        e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.2)";
                        e.currentTarget.style.color = "var(--color-text-muted)";
                        e.currentTarget.style.background = "transparent";
                      }}
                    />
                  }
                >
                  <i className="bx bx-trash"></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("admin.layout.deleteProject")}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      </div>

      {/* Stats Grid */}
      <TooltipProvider delay={200}>
        <div
          data-testid={`admin-project-stats-grid-${proj.id}`}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "10px",
            marginBottom: "15px",
          }}
        >
          {stats.map((stat) => (
            <Tooltip key={stat.key}>
              <TooltipTrigger
                render={
                  <div
                    data-testid={`admin-project-stat-${stat.key}-${proj.id}`}
                    style={{
                      background: "rgba(255, 255, 255, 0.02)",
                      border: "1px solid var(--color-panel-border)",
                      borderRadius: "10px",
                      padding: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      cursor: "help",
                      position: "relative",
                    }}
                  />
                }
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <i
                    className={`bx ${stat.icon}`}
                    style={{ color: stat.color, fontSize: "0.9rem" }}
                  ></i>
                  <span
                    style={{
                      fontSize: "0.6rem",
                      color: "var(--color-text-muted)",
                      fontWeight: "bold",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      flex: 1,
                    }}
                  >
                    {stat.label}
                  </span>
                  <i
                    className="bx bx-info-circle"
                    style={{ color: "rgba(255,255,255,0.15)", fontSize: "0.7rem" }}
                  ></i>
                </div>
                <div
                  data-testid={`admin-project-stat-value-${stat.key}-${proj.id}`}
                  style={{ fontSize: "1.3rem", fontWeight: "900", color: stat.color }}
                >
                  {formatNumber(stat.value)}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{stat.info}</p>
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>

      {/* Cost / Usage Row */}
      <div
        data-testid={`admin-project-cost-row-${proj.id}`}
        style={{
          display: "flex",
          gap: "15px",
          alignItems: "center",
          padding: "12px 15px",
          background: "rgba(16, 185, 129, 0.03)",
          border: "1px solid rgba(16, 185, 129, 0.1)",
          borderRadius: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
          <i className="bx bx-dollar-circle" style={{ color: "#10B981", fontSize: "1.2rem" }}></i>
          <div>
            <div
              style={{
                fontSize: "0.6rem",
                color: "var(--color-text-muted)",
                fontWeight: "bold",
                letterSpacing: "0.5px",
              }}
            >
              AI CHAT SESSIONS COST ({proj.usage?.period || "N/A"})
            </div>
            <div
              data-testid={`admin-project-cost-value-${proj.id}`}
              style={{ fontSize: "1.1rem", fontWeight: "800", color: "#10B981" }}
            >
              {formatCost(proj.usage?.estimatedCost || 0)} USD
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "15px" }}>
          {Object.entries(proj.usage?.services || {}).map(([service, data]) => (
            <div
              key={service}
              data-testid={`admin-project-service-${service}-${proj.id}`}
              style={{ textAlign: "center" }}
            >
              <div
                style={{
                  fontSize: "0.55rem",
                  color: "var(--color-text-muted)",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {service.replace("_", " ")}
              </div>
              <div
                style={{
                  fontSize: "0.9rem",
                  fontWeight: "700",
                  color: "var(--color-text-primary)",
                }}
              >
                {formatNumber((data as any)?.calls || 0)}{" "}
                <span style={{ fontSize: "0.6rem", color: "var(--color-text-muted)" }}>calls</span>
              </div>
            </div>
          ))}
          {Object.keys(proj.usage?.services || {}).length === 0 && (
            <span
              style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", fontStyle: "italic" }}
            >
              No activity this month
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
